import { createSelector } from 'reselect';

import { fullApplicationSelector } from './application-selectors';

import { RequestState, StateDictionary } from './utils/state-manager';
import { NotificationType } from '../util/notification-types';

type JobDictionary = { [jobId: number]: Job };
type DeletedJobDictionary = { [orgId: number]: Job[] };
type JobDetailsDictionary = { [jobId: number]: JobDetails };
type JobAutomatedLabelsDictionary = { [jobId: number]: Array<AutomatedLabel> };
type AdSetDictionary = { [jobId: number]: Array<JobFacebookAdSet> };
type ApplicantSummaryDictionary = { [jobId: number]: PerformancePageData };
type JobRefreshDateDictionary = { [jobId: number]: JobRefreshDate };
type JobUsersDictionary = { [jobId: number]: User[] | undefined };
type NotificationSettingDictionary = { [orgId: number]: NotificationSetting[] };

export type JobState = {
  jobsById: JobDictionary;
  jobsStateById: StateDictionary;
  jobsStateByOrgId: StateDictionary;
  jobsStateByAccountId: StateDictionary;
  jobDetailsByJobId: JobDetailsDictionary;
  labelsByJobId: JobAutomatedLabelsDictionary;
  labelsByJobIdStatus: StateDictionary;
  commonFinalizingHireSteps: Array<FinalizingHireStep>;
  commonFinalizingHireStepsStatus: RequestState;
  applicantSummaryByJobId: ApplicantSummaryDictionary;
  applicantSummaryByJobIdStatus: StateDictionary;
  usersByJobId: JobUsersDictionary;
  usersByJobIdStatus: StateDictionary;
  jobDetailsByJobIdStatus: StateDictionary;
  adSetsByJobId: AdSetDictionary;
  notificationTypes: NotificationType[] | null;
  notificationTypesLoading: boolean;
  jobRefreshDatesById: JobRefreshDateDictionary;
  jobRefreshDatesStateByOrgId: StateDictionary;
  deletedJobsByOrgId: DeletedJobDictionary;
  deletedJobsStateByOrgId: StateDictionary;
  orgDefaultNotificationSettingsByOrgId: NotificationSettingDictionary;
  orgDefaultNotificationSettingsStateByOrgId: StateDictionary;
};
type State = { jobs: JobState };

export const jobsByIdSelector = (state: State) => state.jobs.jobsById;
export const jobDetailsByJobIdSelector = (state: State) => state.jobs.jobDetailsByJobId;
export const labelsByJobIdSelector = (state: State) => state.jobs.labelsByJobId;
export const labelsByJobIdStateSelector = (state: State) => state.jobs.labelsByJobIdStatus;
export const commonFinalizingHireStepsSelector = (state: State) =>
  state.jobs.commonFinalizingHireSteps;
export const adSetsByJobIdSelector = (state: State) => state.jobs.adSetsByJobId;

export const jobSelector = createSelector(
  [jobsByIdSelector, (_state: State, jobId: number) => jobId],
  (jobsById, jobId) => (jobsById ? jobsById[jobId] : null),
);

export const jobDetailsSelector = createSelector(
  [jobDetailsByJobIdSelector, (_state: State, jobId: number) => jobId],
  (detailsByJobId, jobId) => detailsByJobId[jobId],
);
export const jobLabelsSelector = createSelector(
  [labelsByJobIdSelector, (_state: State, jobId: number) => jobId],
  (labelsByJobId, jobId) => (labelsByJobId ? labelsByJobId[jobId] : null),
);

export const jobLabelStateForJobIdSelector = createSelector(
  [labelsByJobIdStateSelector, (_state: State, jobId: number) => jobId],
  (labelStateByJobId, jobId) => labelStateByJobId[jobId],
);

export const jobsSelector = createSelector([jobsByIdSelector], (jobsById) =>
  jobsById ? Object.values(jobsById) : null,
);
const jobsStateByIdSelector = (state: State) => state.jobs.jobsStateById;
export const jobsStateForIdSelector = createSelector(
  [jobsStateByIdSelector, (_state: State, jobId: number) => jobId],
  (jobsStateById, jobId) => jobsStateById[jobId],
);

export const accountJobsSelector = createSelector(
  [jobsSelector, (_state: State, accountId: number) => accountId],
  (jobs: Array<Job> | null, accountId: number) =>
    jobs ? jobs.filter((job) => job.org.accountId === accountId) : null,
);
const jobsStateByAccountIdSelector = (state: State) => state.jobs.jobsStateByAccountId;
export const jobsStateForAccountIdSelector = createSelector(
  [jobsStateByAccountIdSelector, (_state: State, accountId: number) => accountId],
  (jobsStateByAccountId, accountId) => jobsStateByAccountId[accountId],
);

export const jobsForOrgIdSelector = createSelector(
  [jobsSelector, (_state: State, orgId: number) => orgId],
  (jobs, orgId) => (jobs ? jobs.filter((job) => job.orgId === orgId) : null),
);
const jobsStateByOrgIdSelector = (state: State) => state.jobs.jobsStateByOrgId;
export const jobsStateForOrgIdSelector = createSelector(
  [jobsStateByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (jobsStateByOrgId, orgId) => jobsStateByOrgId[orgId],
);

export const jobForJobApplicantIdSelector = createSelector(
  [jobsByIdSelector, fullApplicationSelector],
  (jobsById: JobDictionary, fullApplication: FullApplicant | null) =>
    jobsById && fullApplication ? jobsById[fullApplication.job_id] : null,
);

export const applicantSummaryByJobIdStateSelector = (state: State) =>
  state.jobs.applicantSummaryByJobIdStatus;
export const applicantSummaryStateForJobIdSelector = createSelector(
  [applicantSummaryByJobIdStateSelector, (_state: State, jobId: number) => jobId],
  (applicantSummaryByJobId, jobId) => applicantSummaryByJobId[jobId],
);

export const applicantSummaryByJobIdSelector = (state: State) => state.jobs.applicantSummaryByJobId;
export const applicantSummaryForJobIdSelector = createSelector(
  [applicantSummaryByJobIdSelector, (_state: State, jobId: number) => jobId],
  (applicantSummaryByJobId, jobId) => applicantSummaryByJobId[jobId],
);

export const commonFinalizingHireStepsStateSelector = (state: State) =>
  state.jobs.commonFinalizingHireStepsStatus;

const usersByJobIdSelector = (state: State) => state.jobs.usersByJobId;
export const usersForJobIdSelector = createSelector(
  [usersByJobIdSelector, (_state: State, jobId: number) => jobId],
  (usersByJobId, jobId) => usersByJobId[jobId],
);

export const usersByJobIdStateSelector = (state: State) => state.jobs.usersByJobIdStatus;
export const usersStateForJobIdSelector = createSelector(
  [usersByJobIdStateSelector, (_state: State, jobId: number) => jobId],
  (usersByJobIdState, jobId) => usersByJobIdState[jobId],
);

export const jobDetailsByJobIdStateSelector = (state: State) => state.jobs.jobDetailsByJobIdStatus;
export const jobDetailsStateForJobIdSelector = createSelector(
  [jobDetailsByJobIdStateSelector, (_state: State, jobId: number) => jobId],
  (jobDetailsByJobIdState, jobId) => jobDetailsByJobIdState[jobId],
);

export const adSetsForJobIdSelector = createSelector(
  [adSetsByJobIdSelector, (_state: State, jobId: number) => jobId],
  (adSetsForJobIdState, jobId) => adSetsForJobIdState[jobId],
);

export const jobRefreshDatesByIdSelector = (state: State) => state.jobs.jobRefreshDatesById;
export const jobRefreshDatesForOrgIdSelector = createSelector(
  [jobRefreshDatesByIdSelector, (_state: State, orgId: number) => orgId],
  (jobRefreshDatesById: JobRefreshDateDictionary, orgId: number) =>
    Object.values(jobRefreshDatesById).filter((j) => j.orgId === orgId),
);
export const jobRefreshDateForJobIdSelector = createSelector(
  [jobRefreshDatesByIdSelector, (_state: State, jobId: number) => jobId],
  (jobRefreshDatesById: JobRefreshDateDictionary, jobId: number) => jobRefreshDatesById[jobId],
);

export const jobRefreshDatesStateByOrgIdSelector = (state: State) =>
  state.jobs.jobRefreshDatesStateByOrgId;
export const jobRefreshDatesStateForOrgIdSelector = createSelector(
  [jobRefreshDatesStateByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (jobRefreshDatesStateByOrgId: StateDictionary, orgId: number) =>
    jobRefreshDatesStateByOrgId[orgId],
);

const deletedJobsByOrgIdSelector = (state: State) => state.jobs.deletedJobsByOrgId;
export const deletedJobsForOrgIdSelector = createSelector(
  [deletedJobsByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (deletedJobsByOrgId, orgId) => deletedJobsByOrgId[orgId],
);
const deletedJobsStateByOrgIdSelector = (state: State) => state.jobs.deletedJobsStateByOrgId;
export const deletedJobsStateForOrgIdSelector = createSelector(
  [deletedJobsStateByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (deletedJobsStateByOrgId, orgId) => deletedJobsStateByOrgId[orgId],
);

const orgDefaultNotificationSettingStateByOrgIdSelector = (state: State) =>
  state.jobs.orgDefaultNotificationSettingsStateByOrgId;
export const orgDefaultNotificationSettingsStateForOrgIdSelector = createSelector(
  [orgDefaultNotificationSettingStateByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (orgDefaultNotificationSettingsStateByOrgId, orgId) =>
    orgDefaultNotificationSettingsStateByOrgId[orgId],
);

const orgDefaultNotificationSettingsByOrgIdSelector = (state: State) =>
  state.jobs.orgDefaultNotificationSettingsByOrgId;
export const activeOrgDefaultNotificationSettingsForOrgIdSelector = createSelector(
  [orgDefaultNotificationSettingsByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (orgDefaultNotificationSettingsByOrgId, orgId) =>
    orgDefaultNotificationSettingsByOrgId[orgId]?.reduce((map, setting) => {
      if (setting.isOn !== false) {
        map[setting.username] = {
          ...(map[setting.username] || []),
          [setting.notificationType]: true,
        };
      }
      return map;
    }, {} as { [username: string]: { [notificationType: string]: boolean } }),
);
